import {
  Box,
  CardMedia,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Popover,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';

import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ShareIcon from '@mui/icons-material/ShortcutOutlined';
// import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { ContentMembership } from '@optimus/models';
import {
  ContentFeedSection, ContentInteractionAction, ContentLikeStatus, MetaItem,
} from '../../../common/models/content/Content';
import { useAppSelector } from '../../../store/hooks';
import { selectContentList, selectIsContentPreview } from '../../../store/slices/contentSlice';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import ContentShareSheet from './ContentShareSheet';
import { TestIds } from '../../../assets/testIds';

interface ContentFooterViewProps {
  contentId: number;
  contentLikeStatus?: ContentLikeStatus;
  contentTitle?: string;
  contentCreatorName?: string | null;
  logo?: string | null;
  OnclickListener?: () => void;
  contentMetadata?: MetaItem[];
  contentType?: string
  contentFeedSection?: ContentFeedSection;
  contentFeedSectionPosition?: number;
  locked?: boolean;
  lowestMembershipRequired?: ContentMembership;
}

const useStyles = makeStyles((theme: Theme) => ({
  shareDialog: {
    '& .MuiPaper-root': {
      borderRadius: theme.spacing(2),
      margin: theme.spacing(3),
    },
    '& .MuiDialog-paper': {
      width: '800px',
    },
    '& .MuiDialogContent-root': {
      paddingBottom: theme.spacing(5),
      [theme.breakpoints.down('sm')]: {
        paddingBottom: theme.spacing(8),
      },
    },
    '& .MuiDialogActions-root': {
      padding: theme.spacing(1),
    },
  },
  podcastChannelDialog: {
    '& .MuiPaper-root': {
      borderRadius: theme.spacing(2),
      margin: theme.spacing(3),
    },
    '& .MuiDialog-paper': {
      width: '500px',
    },
    '& .MuiDialogContent-root': {
      padding: '0 0 40px 24px',
      [theme.breakpoints.down('sm')]: {
        padding: '0 0 64px 0',
      },
    },
    '& .MuiDialogActions-root': {
      padding: theme.spacing(1),
    },
  },
}));

const actionIconButtonStyle = (theme: Theme) => ({
  height: theme.spacing(5.25),
  width: theme.spacing(5.25),
  padding: theme.spacing(1.25),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: theme.spacing(12.5),
  border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
  boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05), 0px 0px 0px 0px rgba(0, 0, 0, 0.00), 0px 0px 0px 0px rgba(0, 0, 0, 0.00)',
  cursor: 'pointer',
});

const popoverButtonStyle = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  gap: theme.spacing(0.625),
  padding: theme.spacing(1.25),
  cursor: 'pointer',
  boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05), 0px 0px 0px 0px rgba(0, 0, 0, 0.00), 0px 0px 0px 0px rgba(0, 0, 0, 0.00)',
  borderBottom: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
});

export default function ContentFooterView({
  contentId,
  contentLikeStatus,
  contentTitle,
  contentCreatorName,
  logo,
  OnclickListener,
  contentMetadata,
  contentType,
  contentFeedSection,
  contentFeedSectionPosition,
  locked,
}: ContentFooterViewProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const contentList = useAppSelector(selectContentList);
  const content = contentList.find((item) => item.id === contentId);
  const [isSaved, setIsSaved] = useState(contentLikeStatus?.save);
  const [isLiked, setIsLiked] = useState(contentLikeStatus?.like);
  const [isDisliked, setIsDisliked] = useState(contentLikeStatus?.dislike);
  const [shareUrl, setShareUrl] = useState('');
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  // const [isPodcast, setIsPodcast] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('Share');
  const [listenSubscribeSheetOpen, setListenSubscribeSheetOpen] = useState(false);
  const [moreOptionsPopover, setMoreOptionsPopover] = useState<HTMLElement | null>(null);
  const isPreview = useAppSelector(selectIsContentPreview);
  const classes = useStyles();
  const { trackContentInteraction } = useCustomEventTracking();

  useEffect(() => {
    if (contentLikeStatus) {
      setIsLiked(contentLikeStatus.like);
      setIsDisliked(contentLikeStatus.dislike);
      setIsSaved(contentLikeStatus.save);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentLikeStatus]);

  useEffect(() => {
    if (content?.attributes?.type.toUpperCase() === 'VIDEO') {
      setShareUrl(content.attributes?.video_url || '');
    } else if (content?.attributes?.type.toUpperCase() === 'PODCAST') {
      // setIsPodcast(true);
      setDialogTitle('Listen & Subscribe');
    } else {
      setShareUrl(content?.attributes?.external_link?.target || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fireContentInteractionEvent = (action: ContentInteractionAction, options?: {
    [k: string]: unknown;
  }) => {
    trackContentInteraction(
      action,
      contentType?.replace(/^\w/, (c) => c.toUpperCase()) || '',
      {
        contentId,
        contentTitle: contentTitle || '',
        contentCreatorName: contentCreatorName || '',
        contentMetaData: contentMetadata || [],
        type: contentType || '',
        action,
        ...(contentFeedSection && { contentFeedSection }),
        ...(contentFeedSectionPosition && { contentFeedSectionPosition }),
        ...options,
      },
    );
  };

  const handleContentInteraction = (action: ContentInteractionAction) => {
    switch (action) {
      case ContentInteractionAction.Like:
      // handle mutual exclusiveness
        if (isDisliked) {
          setIsDisliked(false);
        }
        setIsLiked(true);
        // fire like event
        fireContentInteractionEvent(ContentInteractionAction.Like);
        break;
      case ContentInteractionAction.RemoveLike:
        setIsLiked(false);
        // fire remove like event
        fireContentInteractionEvent(ContentInteractionAction.RemoveLike);
        break;
      case ContentInteractionAction.Dislike:
      // handle mutual exclusiveness
        if (isLiked) {
          setIsLiked(false);
        }
        setIsDisliked(true);
        // fire dislike event
        fireContentInteractionEvent(ContentInteractionAction.Dislike);
        break;
      case ContentInteractionAction.RemoveDislike:
        setIsDisliked(false);
        // fire remove dislike event
        fireContentInteractionEvent(ContentInteractionAction.RemoveDislike);
        break;
      case ContentInteractionAction.Save:
        setIsSaved(true);
        // fire save event
        fireContentInteractionEvent(ContentInteractionAction.Save);
        break;
      case ContentInteractionAction.Unsave:
        setIsSaved(false);
        // fire unsave event
        fireContentInteractionEvent(ContentInteractionAction.Unsave);
        break;
      default:
      // fire event with 'action' input parameter interaction type
        fireContentInteractionEvent(action);
        break;
    }
  };

  const handleLikeIcon = () => {
    const action = isLiked ? ContentInteractionAction.RemoveLike : ContentInteractionAction.Like;
    handleContentInteraction(action);
  };

  const handleDislikeIcon = () => {
    const action = isDisliked ? ContentInteractionAction.RemoveDislike :
      ContentInteractionAction.Dislike;
    handleContentInteraction(action);
  };

  const handleSaveIcon = () => {
    const action = isSaved ? ContentInteractionAction.Unsave : ContentInteractionAction.Save;
    handleContentInteraction(action);
  };

  const openShareSheet = () => {
    setShareSheetOpen(true);
  };

  const handleSheetClose = () => {
    setShareSheetOpen(false);
    setListenSubscribeSheetOpen(false);
  };

  const handleMoreOptionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoreOptionsPopover(event.currentTarget);
  };

  const handleMoreOptionsClose = () => {
    setMoreOptionsPopover(null);
  };

  const renderPopover = (popoverId: string) => (
    <Popover
      id={popoverId}
      open={Boolean(moreOptionsPopover)}
      anchorEl={moreOptionsPopover}
      onClose={handleMoreOptionsClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Box
        sx={{
          display: 'inline-flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          borderRadius: theme.spacing(1.5),
          border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
        }}
      >
        <Box
          data-testid={TestIds.contentFeed.showMoreLikeThisBtn}
          sx={popoverButtonStyle}
          onClick={handleLikeIcon}
        >
          {isLiked ?
            <ThumbUpIcon sx={{ color: theme.palette.brand.primary }} fontSize="small" titleAccess='Like' /> :
            <ThumbUpOutlinedIcon sx={{ color: theme.palette.grey[500] }} fontSize="small" titleAccess='Like' />
          }
          <Typography variant='body7'>
            {t('content.action.showMore')}
          </Typography>
        </Box>
        <Box
          data-testid={TestIds.contentFeed.showLessLikeThisBtn}
          sx={popoverButtonStyle}
          onClick={handleDislikeIcon}
        >
          {isDisliked ?
            <ThumbDownIcon sx={{ color: theme.palette.brand.primary }} fontSize="small" titleAccess='Like' /> :
            <ThumbDownOutlinedIcon sx={{ color: theme.palette.grey[500] }} fontSize="small" titleAccess='Like' />
          }
          <Typography variant='body7'>
            {t('content.action.showLess')}
          </Typography>
        </Box>
        <Box sx={popoverButtonStyle} onClick={openShareSheet}>
          <ShareIcon sx={{ color: theme.palette.grey[500] }} fontSize="small" titleAccess='Share' />
          <Typography variant='body7'>
            {t('content.action.sharePost')}
          </Typography>
        </Box>
      </Box>
    </Popover>
  );

  return (
    <>
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        cursor: 'pointer',
      }}
      // excluding event handler in case of article as we already have it on parent div
      onClick={contentType?.toUpperCase() !== 'ARTICLE' ? OnclickListener : undefined}
      >
        {logo ? <CardMedia
          sx={{
            height: theme.spacing(4.5),
            width: theme.spacing(4.5),
            borderRadius: theme.spacing(3.75),
            border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
            p: theme.spacing(1.875),
            marginRight: theme.spacing(1.25),
            boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05), 0px 0px 0px 0px rgba(0, 0, 0, 0.00), 0px 0px 0px 0px rgba(0, 0, 0, 0.00)',
          }}
          image={logo || ''}
        /> : <Box sx={{
          height: theme.spacing(4),
          width: theme.spacing(4),
        }}></Box>}
        <Box><Typography
          variant='h6'
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >{contentCreatorName}</Typography></Box>
      </Box>
      {locked && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: theme.spacing(0.625),
            [theme.breakpoints.down('sm')]: {
              mt: theme.spacing(-0.25),
            },
          }}
        >
          <IconButton sx={actionIconButtonStyle}
            onClick={(e) => { e.stopPropagation(); handleMoreOptionsClick(e); }}
            aria-describedby={`more-options-popover-${contentId}`}>
            <MoreHorizIcon sx={{ color: theme.palette.grey[500] }} fontSize="small" titleAccess='More' />
          </IconButton>
          {renderPopover(`more-options-popover-${contentId}`)}
          <IconButton sx={actionIconButtonStyle}
            onClick={(e) => { e.stopPropagation(); handleSaveIcon(); }}>
            {isSaved ?
              <BookmarkIcon sx={{ color: theme.palette.brand.primary }} fontSize="small" titleAccess='Save' /> :
              <BookmarkBorderOutlinedIcon sx={{ color: theme.palette.grey[500] }} fontSize="small" titleAccess='Save' />
            }
          </IconButton>
        </Box>
      )}
      {!locked && isPreview && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: theme.spacing(0.625),
            [theme.breakpoints.down('sm')]: {
              mt: theme.spacing(-0.25),
            },
          }}
        >
          <IconButton disabled sx={actionIconButtonStyle}>
            <MoreHorizIcon sx={{ color: theme.palette.grey[500] }} fontSize="small" titleAccess='More' />
          </IconButton>
          <IconButton disabled sx={actionIconButtonStyle}>
            <BookmarkIcon sx={{ color: theme.palette.grey[500] }} fontSize="small" titleAccess='Save' />
          </IconButton>
        </Box>
      )}
      {!locked && !isPreview && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: theme.spacing(0.625),
            [theme.breakpoints.down('sm')]: {
              mt: theme.spacing(-0.25),
            },
          }}
        >
          <IconButton sx={actionIconButtonStyle}
            onClick={(e) => { e.stopPropagation(); handleMoreOptionsClick(e); }}
            aria-describedby={`more-options-popover-${contentId}`}>
            <MoreHorizIcon sx={{ color: theme.palette.grey[500] }} fontSize="small" titleAccess='More' />
          </IconButton>
          {renderPopover(`more-options-popover-${contentId}`)}
          <IconButton sx={actionIconButtonStyle}
            onClick={(e) => { e.stopPropagation(); handleSaveIcon(); }}>
            {isSaved ?
              <BookmarkIcon sx={{ color: theme.palette.brand.primary }} fontSize="small" titleAccess='Save' /> :
              <BookmarkBorderOutlinedIcon sx={{ color: theme.palette.grey[500] }} fontSize="small" titleAccess='Save' />
            }
          </IconButton>
        </Box>
      )}
      <Dialog
        className={shareSheetOpen ? classes.shareDialog : classes.podcastChannelDialog}
        aria-labelledby="customized-dialog-title"
        open={shareSheetOpen || listenSubscribeSheetOpen}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle sx={{
          m: 0,
          p: 2,
          [theme.breakpoints.down('sm')]: {
            ml: theme.spacing(3),
          },
        }}>
          <Grid container margin={0} marginTop={1}>
            <Grid item xs={10} >
              <Typography sx={{
                fontSize: theme.spacing(2.5),
                fontWeight: '500',
                ml: theme.spacing(3),
                [theme.breakpoints.down('sm')]: {
                  ml: 0,
                },
              }}>{dialogTitle}</Typography>
            </Grid>
            <Grid item xs={2}>
              <IconButton
                aria-label="close"
                onClick={handleSheetClose}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 15,
                  color: 'black',
                }}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <ContentShareSheet shareUrl={shareUrl}
            fireContentInteractionEvent={fireContentInteractionEvent} />
        </DialogContent>
      </Dialog>
    </>
  );
}
